import {
  buildHeaderData,
  HEADER_COMBINATIONS,
} from "../../../components/performances/headerDefinitions";
import { PERFORMANCES_LIFE_CYCLE_STATUS } from "../../../utils/constants";

// Helper to avoid duplicate control definitions for debounced search inputs
const buildDebouncedSearchControl = (scope, placeholder) => ({
  type: "Control",
  scope,
  component: "searchInput",
  placeholder,
  options: { debounce: true },
});

// Precompute options once for engagement status
const engagementStatusOptions = Object.values(PERFORMANCES_LIFE_CYCLE_STATUS).map(
  (status) => ({ id: status.key, displayText: status.value })
);

// Shared JSON schema fragments
const dateRangeProperties = {
  engagementDateFrom: {
    type: "string",
    format: "date",
    title: "Performance Date From",
  },
  engagementDateTo: {
    type: "string",
    format: "date",
    title: "Performance Date To",
  },
};

const engagementStatusProperty = {
  engagementStatus: {
    items: {
      type: "string",
      options: engagementStatusOptions,
    },
  },
};

// Helpers for non-debounced controls
const buildControl = (scope, label, placeholder, extra = {}) => ({
  type: "Control",
  scope,
  label,
  placeholder,
  ...extra,
});

const buildDateControl = (scope, label, extra = {}) =>
  buildControl(scope, label, undefined, {
    component: "datePickerMaterialUI",
    ...extra,
  });

export const codeScanHeaderData = buildHeaderData(HEADER_COMBINATIONS.codeScan);

export const codeScanFilterJsonSchema = {
  withDebounce: {
    type: "object",
    properties: {
      ids: {
        type: "string",
      },
      engagementIdentifier: {
        type: "string",
      },
      profileIds: {
        type: "array",
      },
      userIds: {
        type: "array",
      },
    },
  },
  withoutDebounce: {
    type: "object",
    properties: {
      ...dateRangeProperties,
      ...engagementStatusProperty,
    },
  },
};

export const codeScanFilterUiSchema = {
  withDebounce: {
    type: "HorizontalLayout",
    elements: [
      buildDebouncedSearchControl("#/properties/ids", "Search by ID"),
      buildDebouncedSearchControl(
        "#/properties/engagementIdentifier",
        "Search by External ID"
      ),
      buildDebouncedSearchControl("#/properties/userIds", "Search by User ID"),
      buildDebouncedSearchControl("#/properties/profileIds", "Search by Profile ID"),
    ],
  },
  withoutDebounce: {
    type: "HorizontalLayout",
    elements: [
      buildDateControl("#/properties/engagementDateFrom", undefined, {
        setToStartOfDay: true,
        maximum: new Date(),
      }),
      buildDateControl("#/properties/engagementDateTo", undefined, {
        minimum: "engagementDateFrom",
        setToEndOfDay: true,
        maximum: new Date(),
      }),
      buildControl("#/properties/engagementStatus", "Status", "Status", {
        component: "multipleSelectPerformanceList",
        selection: "multiple",
      }),
    ],
  },
};

export const codeScanFilterAllJsonSchema = {
  type: "object",
  properties: {
    ids: {
      type: "string",
    },
    engagementIdentifier: {
      type: "string",
    },
    userIds: {
      type: "array",
    },
    profileIds: {
      type: "array",
    },
    channel: {
      type: "string",
    },
    campaignName: {
      type: "string",
    },
    ...dateRangeProperties,
    ...engagementStatusProperty,
  },
};

export const codeScanFilterAllUiSchema = {
  type: "VerticalLayout",
  elements: [
    buildControl("#/properties/ids", "ID", "Search by ID"),
    buildControl(
      "#/properties/engagementIdentifier",
      "External ID",
      "Search by External ID"
    ),
    buildControl("#/properties/userIds", "User ID", "Search by User ID"),
    buildControl("#/properties/profileIds", "Profile ID", "Search by Profile ID"),
    buildControl("#/properties/channel", "Channel", "Search by Channel"),
    buildControl("#/properties/campaignName", "Campaign Name", "Campaign Name", {
      component: "campaignNameSelect",
      selection: "multiple",
    }),
    buildDateControl("#/properties/engagementDateFrom", "Performance Date From", {
      maximum: new Date(),
      setToStartOfDay: true,
    }),
    buildDateControl("#/properties/engagementDateTo", "Performance Date To", {
      minimum: "engagementDateFrom",
      setToEndOfDay: true,
      maximum: new Date(),
    }),
    buildControl("#/properties/engagementStatus", "Status", "Status", {
      component: "multipleSelectPerformanceList",
      selection: "multiple",
    }),
  ],
};
